import React, { memo, useState, useMemo } from 'react';
import {
	Button,
	DatePicker,
	Form,
	Input,
	InputNumber,
	Modal,
	Popconfirm,
	Select,
} from "antd";
import moment from "moment";
import { useDispatch } from "react-redux";
import useParam from "../../../../../../hooks/useParam";
import getAddresses from "../../../../store/thunks/getAddresses";
import getAddressMeters from "../../../../store/thunks/getAddressMeters";
import { useParams } from "react-router";
import editMeter from "../../../../store/thunks/editMeter";
import deleteMeter from "../../../../store/thunks/deleteMeter";
import MeterTagField from "../../../../components/MeterTagField/MeterTagField";
import { useHouseMeterStatusesQuery } from '../../../../../HousingStock/queries/useHouseMeterStatuses';
import { useHouseMeterTariffsQuery } from '../../../../../HousingStock/queries/useHouseMeterTariffs';
import { isDismantledMeterStatus } from '../../../../utils/isDismantledMeterStatus';

const EditMeterPopup = memo(({ meter, onClose }) => {
	const dispatch = useDispatch();

	const [CommissionningDate, setCommissionningDate] = useState(
		meter.commissioningDate ? moment(meter.commissioningDate) : undefined
	);
	const [DecommissionningDate, setDecommissionningDate] = useState(
		meter.decommissioningDate ? moment(meter.decommissioningDate) : undefined
	);
	const [Description, setDescription] = useState(
		meter.description || undefined
	);
	const [Coef, setCoef] = useState(meter.coef || 1);
	const [selectedTag, setSelectedTag] = useState(
		meter.tagList?.[0]?.id || undefined
	);
  const [MeterStatusId, setMeterStatusId] = useState(
    meter.meterStatus?.id || undefined
  );
  const [MeterTariffId, setMeterTariffId] = useState(
    meter.meterTypeTariff?.id || undefined
  );
	const { id: houseId } = useParams();
	const activeMeterTypes = useParam("types", [], (arr) => arr?.map(Number));
	const activeTags = useParam("tags", [], (arr) => arr?.map(Number));
	const search = useParam("search", "");
	const meterCode = useMemo(() => {
		if (!isNaN(Number(search))) {
			return search;
		}
	}, [search]);

  const meterStatusesQuery = useHouseMeterStatusesQuery();
  const meterTariffsQuery = useHouseMeterTariffsQuery();

  const defaultMeterStatusId = useMemo(
    () =>
      meterStatusesQuery.data?.find(
        (mState) => mState.name.toLowerCase() === 'работает'
      ),
    [meterStatusesQuery.data]
  );

	const onEditMeter = async () => {
		await dispatch(
			editMeter(
				meter,
				{
					CommissionningDate: CommissionningDate,
					DecommissionningDate: DecommissionningDate,
					Description: Description,
					Coef: Coef || meter.coef,
					Tag: selectedTag,
          MeterStatusId: MeterStatusId || defaultMeterStatusId?.id,
          ...(isMeterTypeHasTariffs
            ? {
                MeterTariffId: MeterTariffId,
              }
            : {}),
				},
				houseId
			)
		);
		dispatch(
			getAddressMeters(houseId, activeMeterTypes, meterCode, activeTags)
		);
		dispatch(
			getAddresses({
				address: isNaN(Number(search)) ? search : undefined,
				types: activeMeterTypes,
				meterCode,
				allAddress: false,
				tags: activeTags,
			})
		);
		onClose();
	};

	const onDeleteMeter = () => {
		dispatch(deleteMeter(meter, houseId));
	};

  const isDisabledStatus = isDismantledMeterStatus(meter.meterStatus);

  const meterTypeName = meter?.type?.name;
  const isMeterTypeHasTariffs = useMemo(() => {
    if (typeof meterTypeName !== 'string') {
      return false;
    }
    return meterTypeName.toLowerCase() === 'электроэнергия';
  }, [meterTypeName]);

	return (
		<Modal
			visible={true}
			onCancel={onClose}
			footer={
				<div className="edit_meter_row">
					<Popconfirm
						title="Вы действительно хотите удалить счётчик?"
						okText="Ок"
						onConfirm={onDeleteMeter}
					>
						<Button onClick={onDeleteMeter} type="danger">
							Удалить счётчик
						</Button>
					</Popconfirm>
					<Button
						onClick={onEditMeter}
						type="primary"
						disabled={
							!Description ||
							!MeterStatusId ||
							(isMeterTypeHasTariffs && !MeterTariffId) ||
							!Coef
						}
					>
						Изменить
					</Button>
				</div>
			}
			title={`Редактирование счетчика №${meter.meterCode}`}
		>
			<div className="edit_meter_row">
				<Form.Item label="Дата ввода в эксплуатацию" colon={false}>
					<DatePicker
						style={{ width: "100%" }}
						value={CommissionningDate}
						onChange={(date) => setCommissionningDate(date)}
						allowClear={!meter.commissioningDate}
						disabled={isDisabledStatus}
					/>
				</Form.Item>
				<Form.Item label="Дата вывода из эксплуатации" colon={false}>
					<DatePicker
						style={{ width: "100%" }}
						value={DecommissionningDate}
						onChange={(date) => setDecommissionningDate(date)}
						allowClear={!meter.decommissioningDate}
						disabled={isDisabledStatus}
					/>
				</Form.Item>
			</div>
			<div className="edit_meter_row">
				<Form.Item
					label="Расположение"
					colon={false}
					style={{ flexGrow: 1, marginRight: 20 }}
				>
					<Input
						value={Description}
						onChange={(e) => setDescription(e.target.value)}
						disabled={isDisabledStatus}
					/>
				</Form.Item>
				<Form.Item label="Коэффициент" colon={false} style={{ width: 100 }}>
					<InputNumber
						min={0.1}
						step={0.1}
						value={Coef}
						onChange={(value) => setCoef(value)}
						style={{ width: "100%" }}
						disabled={isDisabledStatus}
					/>
				</Form.Item>
			</div>
			<Form.Item label="Тег счётчика" colon={false}>
				<MeterTagField
					onSelect={(value) => setSelectedTag(value)}
					value={selectedTag}
					disabled={isDisabledStatus}
				/>
			</Form.Item>
      <Form.Item
        label="Состояние"
        colon={false}
      >
        <Select
          style={{ width: '240px', maxWidth: '100%' }}
          placeholder="Выберите состояние"
          value={MeterStatusId}
          onChange={(value) => setMeterStatusId(value)}
          loading={meterStatusesQuery.isFetching}
        >
          {meterStatusesQuery.data?.map((mState) => (
            <Select.Option
              key={mState.id}
              value={mState.id}
            >
              {mState.name}
            </Select.Option>
          ))}
        </Select>
      </Form.Item>

      {isMeterTypeHasTariffs && (
        <>
          <Form.Item
            label="Тариф"
            colon={false}
          >
            <Select
              style={{ width: '240px', maxWidth: '100%' }}
              placeholder="Выберите тариф"
              value={MeterTariffId}
              onChange={(value) => setMeterTariffId(value)}
              loading={meterTariffsQuery.isFetching}
              disabled={isDisabledStatus}
            >
              {meterTariffsQuery.data?.map((mTariff) => (
                <Select.Option
                  key={mTariff.id}
                  value={mTariff.id}
                >
                  {mTariff.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </>
      )}
		</Modal>
	);
});

export default EditMeterPopup;
